import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { ChevronDown, CircleCheck, X } from "lucide-react";
import host from "../../global";
import { useTheme } from "../../hooks/useTheme";

const routeMapping = {
  Home: ["hr/dashboard"],
  Reports: ["/hr/candidatereports", "/hr/evaluationpage"],
  Jobs: ["hr/jobvacancies"],
  Activity: ["hr/activity"],
};

const SidebarIcon = ({ iconPath, hoverIconPath, label, path, onClick }) => {
  const location = useLocation();
  const isActive =
    path !== "#" &&
    (location.pathname.includes(path) ||
      (routeMapping[label] &&
        routeMapping[label].some((route) => {
          const included = location.pathname.includes(route);

          return included;
        })));

  return (
    <div className="relative">
      {isActive && (
        <div className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-500 to-blue-500 rounded-l-md bg-bgPrimary "></div>
      )}
      <Link
        to={path}
        className={`flex w-full text-white cursor-pointer rounded-md transition-all group ${
          isActive ? "bg-gradient-to-r from-purple-600/20 to-transparent border-none border-l-2 " : ""
        }`}
        onClick={onClick}
      >
        <div className={`flex flex-col items-center w-full gap-1 p-2 mt-2`}>
          <img
            src={isActive ? hoverIconPath : iconPath}
            alt={label}
            className={`transition-all ${
              label === "Home" && isActive
                ? "w-6 h-6"
                : label === "Settings" && label != "Home"
                ? "w-6 h-6"
                : "w-6 h-6"
            } group-hover:hidden`}
          />
          <img
            src={hoverIconPath}
            alt={`${label} hover`}
            className={`transition-all ${
              label === "Home"
                ? "w-6 h-6"
                : label === "Settings" && label != "Home"
                ? "w-6 h-6"
                : "w-6 h-6"
            } hidden group-hover:block ${isActive ? "block" : ""}`}
          />
          <span
            className={`dark:text-[11px] text-[11px] font-[500] text-center w-full leading-tight dark:opacity-80  ${
              label === "Home" ? "" : "mt-1.5"
            } ${isActive ? "dark:text-purple-400 text-textPrimary" : "dark:text-white text-textPrimary"}`}
          >
            {label}
          </span>
        </div>
      </Link>
    </div>
  );
};
const Sidebar = () => {
  const navigate = useNavigate();
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketTitleError, setTicketTitleError] = useState("");
  const [ticketDescriptionError, setTicketDescriptionError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const ticketModalRef = useRef(null);
  const fileInputRef = useRef(null);
  const accessToken = sessionStorage.getItem("accessToken");
  const [priority, setPriority] = useState("Medium");
  const [isPriorityOpen, setIsPriorityOpen] = useState(false);
  const [notification, setNotification] = useState([]);

  const {theme} = useTheme();

  const sidebarItems = [
    // {
    //   iconPath: "/notificationbell.png",
    //   hoverIconPath: "/notificationbellhover.png",
    //   label: "Activity",
    //   path: "activity",
    // },
    {
      iconPath:`${theme === 'dark' ?  "/homesidebar.png":"/homeSVG.svg"}`,
      hoverIconPath: "/home_hover.png",
      label: "Home",
      path: "dashboard",
    },
    // {
    //   iconPath: "/reportssidebar.png",
    //   hoverIconPath: "/reportssidebarhover.png",
    //   label: "Reports",
    //   path: "candidatereports",
    // },
    {
      iconPath: `${theme === 'dark' ? "/joblibrarysidebar.png" : "/jobsSVG.svg"}`,
      hoverIconPath: "/joblibrarysidebarhover.png",
      label: "Jobs",
      path: "jobvacancies",
    },
  ];

  const priorities = [
    { label: "High", color: "text-red-400", bgColor: "bg-red-500/20" },
    { label: "Medium", color: "text-yellow-400", bgColor: "bg-yellow-500/20" },
    { label: "Low", color: "text-green-400", bgColor: "bg-green-500/20" },
  ];

  const fetchBulkResumeUploadResult = async () => {
    try {
      const response = await axios.get(`${host}/resumes/bulk`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          jd_id: "all"
        }
      });
      const results = response.data.notifications || [];
      results.sort((a, b) => {
        if (a.bulk_upload_date === null && b.bulk_upload_date === null)
          return 0;
        if (a.bulk_upload_date === null) return 1;
        if (b.bulk_upload_date === null) return -1;
        return new Date(b.bulk_upload_date) - new Date(a.bulk_upload_date);
      });
      setNotification(results);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          await new Promise((resolve) => {
            sessionStorage.removeItem("accessToken");
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  useEffect(() => {
    fetchBulkResumeUploadResult();

    const handleNotificationReadStatusUpdated = () => {
      fetchBulkResumeUploadResult();
    };

    window.addEventListener(
      "notificationReadStatusUpdated",
      handleNotificationReadStatusUpdated
    );

    return () => {
      window.removeEventListener(
        "notificationReadStatusUpdated",
        handleNotificationReadStatusUpdated
      );
    };
  }, [accessToken]);

  const toggleTicketModal = () => {
    setIsTicketModalOpen(!isTicketModalOpen);
    setSelectedFile(null);
    setTicketTitle("");
    setTicketDescription("");
    setTicketTitleError("");
    setTicketDescriptionError("");
  };

  // const handleFileChange = (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     const allowedTypes = [
  //       "image/png",
  //       "image/jpeg",
  //       "image/jpg",
  //       "application/pdf",
  //       "application/msword",
  //       "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  //       "application/vnd.ms-excel",
  //       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     ];
  //     if (allowedTypes.includes(file.type)) {
  //       setSelectedFile(file);
  //       toast.info(`Selected file: ${file.name}`);
  //     } else {
  //       setSelectedFile(null);
  //       fileInputRef.current.value = null;
  //       toast.error(
  //         "Invalid file type. Only PNG, JPG, JPEG, DOC, DOCX, PDF, XLSX, XLS allowed."
  //       );
  //     }
  //   }
  // };

  // const validateFields = () => {
  //   let isValid = true;
  //   if (!ticketTitle.trim()) {
  //     setTicketTitleError("Subject is required");
  //     isValid = false;
  //   } else {
  //     setTicketTitleError("");
  //   }
  //   if (!ticketDescription.trim()) {
  //     setTicketDescriptionError("Description is required");
  //     isValid = false;
  //   } else {
  //     setTicketDescriptionError("");
  //   }
  //   return isValid;
  // };

  // const handleSubmit = async () => {
  //   if (!validateFields()) {
  //     return;
  //   }
  //   try {
  //     const response = await axios.post(
  //       `${host}/raiseticket`,
  //       {},
  //       {
  //         params: { title: ticketTitle, description: ticketDescription },
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${accessToken}`,
  //         },
  //       }
  //     );
  //     if (response.status === 200) {
  //       toast.success("Ticket Submitted Successfully");
  //       setTicketTitle("");
  //       setTicketDescription("");
  //       toggleTicketModal();
  //     } else {
  //       toast.error("Error submitting ticket:", response.statusText);
  //     }
  //   } catch (error) {
  //     if (error.response) {
  //       const status = error.response.status;
  //       const detail = error.response.data.detail;
  //       if (status === 401) {
  //         await new Promise((resolve) => {
  //           Cookies.remove("accessToken");
  //           resolve();
  //         });
  //         toast.error(`Unauthorized: ${detail}`);
  //       } else if (status === 404) {
  //         toast.warning(`Not Found: ${detail}`);
  //       } else {
  //         toast.error(`Error: ${detail}`);
  //       }
  //     } else {
  //       toast.error("An unexpected error occurred. Please try again.");
  //     }
  //   }
  // };

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       isTicketModalOpen &&
  //       ticketModalRef.current &&
  //       !ticketModalRef.current.contains(event.target)
  //     ) {
  //       setIsTicketModalOpen(false);
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isTicketModalOpen]);

  const unreadNotificationCount = notification.filter(
    (notif) => !notif.read_status
  ).length;

  return (
    <div
      className="
        bg-app-background bg-bgPrimary border-r border-white/10 
        transition-all duration-300 ease-in-out w-17 overflow-hidden flex flex-col items-center py-3 "
    >
      <div className="flex flex-col gap-4 items-center justify-center py-2">
        <Link to="/hr/dashboard">
          <img
            src="/Homepagelogo.png"
            alt="Logo"
            style={{ height: "34px", width: "24px" }}
          />
        </Link>
        <div className="w-full px-2 space-y-2 py-4">
          {sidebarItems.map((item, index) => (
            <div key={index}>
              {item.label === "Activity" && (
                <div>
                  {unreadNotificationCount > 0 && (
                    <span
                      className="absolute left-[50px] flex items-center justify-center 
                            w-5 h-5 bg-red-600 text-white text-xs font-medium
                            rounded-full border-2 border-gray-900"
                    >
                      {unreadNotificationCount}
                    </span>
                  )}
                </div>
              )}
              <SidebarIcon
                key={index}
                iconPath={item.iconPath}
                hoverIconPath={item.hoverIconPath}
                label={item.label}
                path={item.path}
                onClick={
                  item.label === "Raise Ticket"
                    ? toggleTicketModal
                    : () => navigate(`/hr/${item.path}`)
                }
              />
            </div>
          ))}
        </div>
      </div>
      {/* {isTicketModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{
              backdropFilter: "blur(8px",
              WebkitBackdropFilter: "blur(8px)",
            }}
          ></div>
          <div
            className="w-full max-w-xl p-6 rounded-lg shadow-lg relative"
            ref={ticketModalRef}
            style={{
              background:
                "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              borderRadius: "10px",
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-[#C1A0D0] text-left text-lg">
                Raise a Ticket
              </h3>
              <button onClick={toggleTicketModal}>
                <X className="h-5 w-5 text-[#C1A0D0]" />
              </button>
            </div>
            <div className="flex items-start space-x-5 mt-10">
              <div className="flex flex-col flex-1">
                <span className="text-[#CCCCCC] text-sm">Subject*</span>
                <input
                  type="text"
                  value={ticketTitle}
                  onChange={(e) => {
                    setTicketTitle(e.target.value);
                    if (e.target.value.trim()) {
                      setTicketTitleError("");
                    }
                  }}
                  onBlur={() => {
                    if (!ticketTitle.trim()) {
                      setTicketTitleError("Subject is required");
                    }
                  }}
                  className={`w-full px-3 py-2 bg-transparent border ${
                    ticketTitleError ? "border-red-500" : "border-gray-700/50"
                  } rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none`}
                  placeholder="Enter a subject"
                />
                {ticketTitleError && (
                  <span className="text-red-400 text-xs mt-1">
                    {ticketTitleError}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[#CCCCCC] text-sm">Priority*</span>
                <div className="relative">
                  <button
                    onClick={() => setIsPriorityOpen(!isPriorityOpen)}
                    className="w-36 space-x-5 px-3 py-2 bg-transparent border border-slate-600 rounded text-white flex items-center justify-between mt-2 "
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-sm ${
                          priority === "High"
                            ? "bg-red-500"
                            : priority === "Medium"
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                      ></div>
                      <span className="text-red-400">{priority}</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`text-[#C9A3FF] transition-transform ${
                        isPriorityOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isPriorityOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded shadow-lg z-10">
                      {priorities.map((item) => (
                        <button
                          key={item.label}
                          onClick={() => {
                            setPriority(item.label);
                            setIsPriorityOpen(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-slate-600 flex items-center gap-2 transition-colors"
                        >
                          <div
                            className={`w-2 h-2 rounded-sm ${
                              item.label === "High"
                                ? "bg-red-500"
                                : item.label === "Medium"
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            }`}
                          ></div>
                          <span className={item.color}>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col mt-5">
              <span className="text-[#CCCCCC] text-sm">Description*</span>
              <textarea
                value={ticketDescription}
                onChange={(e) => {
                  setTicketDescription(e.target.value);
                  if (e.target.value.trim()) {
                    setTicketDescriptionError("");
                  }
                }}
                onBlur={() => {
                  if (!ticketDescription.trim()) {
                    setTicketDescriptionError("Description is required");
                  }
                }}
                className={`w-full px-3 py-3 bg-transparent border ${
                  ticketDescriptionError
                    ? "border-red-500"
                    : "border-gray-700/50"
                } rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none`}
                rows="6"
                placeholder="Describe your issue..."
              />
              {ticketDescriptionError && (
                <span className="text-red-400 text-xs mt-1">
                  {ticketDescriptionError}
                </span>
              )}
            </div>
            <div>
              <span className="text-[#CCCCCC] text-sm">Attach File</span>
              <div className="flex items-center space-x-5 py-4">
                <div className="relative">
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="text-[#CCCCCC] bg-[#FFFFFF33] px-2 py-2 border border-gray-700/50 rounded-full w-48 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {selectedFile ? selectedFile.name : "Choose File"}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.xlsx,.xls"
                    className="hidden"
                  />
                </div>
                {selectedFile === null ? (
                  <span className="text-[#A69393] text-xs">
                    Only png/jpg/jpeg/doc/docx/pdf/xlsx/xls files are allowed to
                    upload.
                  </span>
                ) : (
                  <div className="flex items-center space-x-4">
                    <CircleCheck className="w-5 h-5 text-green-400" />
                    <span className="text-[#A69393] text-xs">
                      File Uploaded
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-center pt-4">
              <button
                onClick={handleSubmit}
                className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm"
              >
                Create a Ticket
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default Sidebar;
